
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { Button } from '@/components/ui/button';
import { ArrowRight, Mail, MessageSquare, Users, Phone } from 'lucide-react';

const HeroSection = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  
  // Define the contact button path based on language
  const handleContactClick = () => {
    const contactPath = `/${currentLanguage}/${
      currentLanguage === 'de' ? 'kontakt' : 
      currentLanguage === 'en' ? 'contact' : 
      currentLanguage === 'fr' ? 'contact' : 
      'contatto'
    }`;
    navigate(contactPath);
  };

  return (
    <section className="relative h-[85vh] min-h-[600px] flex items-center justify-center overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full overflow-hidden bg-black z-0">
        <div className="absolute inset-0 w-full h-full z-10 bg-gradient-to-b from-black/70 via-black/50 to-[#030307]"></div>
        <iframe 
          src="https://streamable.com/e/b02fqo?autoplay=1&nocontrols=1&muted=1&loop=1" 
          allow="fullscreen;autoplay" 
          allowFullScreen
          style={{ 
            border: 'none', 
            width: '100%', 
            height: '100%', 
            objectFit: 'cover',
            position: 'absolute',
            left: '0px',
            top: '0px',
            overflow: 'hidden' 
          }}
          title="KI Video Background"
        ></iframe>
      </div>
      
      {/* Content */}
      <div className="container mx-auto px-4 z-20 relative text-center">
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 font-future leading-tight">
          KI-Video Ads für Instagram, <br/>TikTok & Facebook
        </h1>
        
        <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
          {t.socialMediaAdsHero || "Professionelle KI-generierte Videos für Social Media mit nachweisbarer Conversion-Steigerung von bis zu 300%"}
        </p>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
          <Button 
            onClick={handleContactClick}
            className="bg-purple-600 hover:bg-purple-700 text-white font-bold px-8 py-6 text-lg"
            size="lg"
          >
            {t.getStarted || "Jetzt starten"} <ArrowRight className="ml-2" />
          </Button>
        </div>
        
        {/* Contact Features with Icons - SEO optimized section */}
        <div className="mt-16 bg-black/40 backdrop-blur-md p-6 rounded-xl border border-purple-500/20 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-6">KI-Video Marketing Expertise für Ihren Erfolg</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
            <div className="flex flex-col items-center md:items-start space-y-3 p-4 hover:bg-purple-900/20 rounded-lg transition-colors">
              <div className="bg-purple-600/30 p-3 rounded-full">
                <MessageSquare className="w-6 h-6 text-purple-300" />
              </div>
              <h3 className="font-semibold text-white text-lg">Persönliche Beratung</h3>
              <p className="text-gray-300 text-sm">Individuelles Strategiegespräch für Ihre Social Media Video Kampagne</p>
            </div>
            
            <div className="flex flex-col items-center md:items-start space-y-3 p-4 hover:bg-purple-900/20 rounded-lg transition-colors">
              <div className="bg-purple-600/30 p-3 rounded-full">
                <Users className="w-6 h-6 text-purple-300" />
              </div>
              <h3 className="font-semibold text-white text-lg">Zielgruppenfokus</h3>
              <p className="text-gray-300 text-sm">KI-optimierte Videos speziell für Ihre Schweizer Zielgruppe</p>
            </div>
            
            <div className="flex flex-col items-center md:items-start space-y-3 p-4 hover:bg-purple-900/20 rounded-lg transition-colors">
              <div className="bg-purple-600/30 p-3 rounded-full">
                <Phone className="w-6 h-6 text-purple-300" />
              </div>
              <h3 className="font-semibold text-white text-lg">24h Support</h3>
              <p className="text-gray-300 text-sm">Schnelle Antwort innerhalb eines Werktages für Ihre Video-Anfrage</p>
            </div>
            
            <div className="flex flex-col items-center md:items-start space-y-3 p-4 hover:bg-purple-900/20 rounded-lg transition-colors">
              <div className="bg-purple-600/30 p-3 rounded-full">
                <Mail className="w-6 h-6 text-purple-300" />
              </div>
              <h3 className="font-semibold text-white text-lg">Kostenlose Analyse</h3>
              <p className="text-gray-300 text-sm">Gratis Video-Marketing-Analyse für Ihren Social Media Auftritt</p>
            </div>
          </div>
          
          <div className="mt-8">
            <Button
              onClick={handleContactClick}
              className="bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 text-white px-8 py-5 rounded-lg shadow-lg group"
            >
              Kontakt aufnehmen
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
        
        {/* Trust badges */}
        <div className="mt-16 flex flex-wrap justify-center gap-4">
          <div className="px-4 py-2 bg-black/30 backdrop-blur-sm rounded-full border border-purple-500/20">
            <span className="text-white text-sm font-medium">300% ROI-Steigerung</span>
          </div>
          <div className="px-4 py-2 bg-black/30 backdrop-blur-sm rounded-full border border-purple-500/20">
            <span className="text-white text-sm font-medium">KI-Video Expertise</span>
          </div>
          <div className="px-4 py-2 bg-black/30 backdrop-blur-sm rounded-full border border-purple-500/20">
            <span className="text-white text-sm font-medium">70% Kostenersparnis</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
